import { getConfig } from '@/config';

import type {
  AnalyticsAPI,
  AnalyticsConfig,
  AnalyticsDestination,
  PageViewFn,
  TrackFn,
} from './types.js';

const { environment, mode } = getConfig();

const isTestEnv = mode === 'test';
const isDevEnv = mode === 'development';
const isStageEnv = environment === 'stage';

/**
 * Unified analytics service that manages multiple destinations
 *
 * Intended for use when outside of the context of React and use-analytics hook is not an option.
 *
 * This service provides a centralized interface for tracking events, pageviews, and user properties
 * across multiple analytics platforms. It automatically handles destination initialization based on
 * the current environment and provides error isolation to ensure app stability.
 *
 * **Environment Behavior:**
 * - **Test**: No analytics tracking (silent)
 * - **Development**: Console logging only
 * - **Staging without Amplitude key**: Console logging only
 * - **Staging with Amplitude key / Production**: Amplitude when API key is set
 *
 * **Usage:**
 * ```typescript
 * import { Analytics } from '@/lib/analytics/analytics';
 *
 * // Initialize with config
 * Analytics.initialize({
 *   Amplitude: 'your-api-key'
 * });
 *
 * // Track events
 * Analytics.track('button_clicked', { buttonName: 'submit', page: 'checkout' });
 *
 * // Track pageviews
 * Analytics.pageview({ path: '/dashboard', title: 'Dashboard' });
 *
 * // Set user properties
 * Analytics.setUser({ userId: '123', plan: 'premium' });
 * ```
 *
 * @see {@link AnalyticsAPI} - Interface definition
 * @see {@link AnalyticsConfig} - Configuration type
 * @see {@link TrackFn} - Event tracking function type
 * @see {@link PageViewFn} - Pageview tracking function type
 */
class AnalyticsService implements AnalyticsAPI {
  private initialized = false;
  private destinations: AnalyticsDestination[] = [];
  private isInternalUserMode = false;
  /** True when Amplitude remote destination is active (prod or stage with API key). */
  private hasRemoteAmplitude = false;

  /**
   * Initialize all analytics destinations based on the current environment
   *
   * This method sets up the appropriate analytics destinations:
   * - In development: Console destination for debugging
   * - On staging without Amplitude API key in config: Console only
   * - On staging with API key, or production: Amplitude when configured
   * - In test: No initialization (silent)
   *
   * **Note:** This method is idempotent - calling it multiple times will not reinitialize.
   *
   * @param config - Configuration object containing API keys and settings for each destination
   * @param config.Amplitude - Amplitude configuration
   *
   * @example
   * ```typescript
   * Analytics.initialize({
   *   Amplitude: 'your-amplitude-key'
   * });
   * ```
   */
  async initialize(config: AnalyticsConfig): Promise<void> {
    if (isTestEnv || this.initialized) return;

    const amplitudeEnabled =
      typeof config.Amplitude === 'string' && config.Amplitude.length > 0;

    // Local Vite dev: console only (avoid accidental prod/stage traffic)
    if (isDevEnv) {
      const { ConsoleAnalyticsDestination } = await import(
        './destinations/console'
      );
      const consoleDestination = new ConsoleAnalyticsDestination();
      consoleDestination.initialize('hello, localhost!');
      this.destinations.push(consoleDestination);
      this.initialized = true;
      return;
    }

    // Hosted staging without Amplitude key: console only (legacy behavior)
    if (isStageEnv && !amplitudeEnabled) {
      const { ConsoleAnalyticsDestination } = await import(
        './destinations/console'
      );
      const consoleDestination = new ConsoleAnalyticsDestination();
      consoleDestination.initialize('hello, localhost!');
      this.destinations.push(consoleDestination);
      this.initialized = true;
      return;
    }

    if (amplitudeEnabled) {
      const { AmplitudeAnalyticsDestination } = await import(
        './destinations/amplitude'
      );
      const amplitudeDestination = new AmplitudeAnalyticsDestination();
      amplitudeDestination.initialize(config.Amplitude);
      this.destinations.push(amplitudeDestination);
      this.hasRemoteAmplitude = true;
    }

    this.initialized = true;
  }

  /**
   * Configure analytics for internal users by switching to console-only destinations
   *
   * This method should be called after user authentication when an internal user is detected.
   * In local dev or staging without remote Amplitude: No destination swap (console only)
   * When remote Amplitude is active: Replaces Amplitude with console only
   *
   * **Note:** This method can be called multiple times safely and will not accumulate destinations.
   * **Note:** Currently uses is_admin as a proxy for internal users. Consider adding
   * a dedicated is_internal field to the user model for more precise control.
   *
   * @example
   * ```typescript
   * // After user authentication
   * if (user.is_admin) { // or user.is_internal when available
   *   Analytics.setInternalUser();
   * }
   * ```
   */
  async setInternalUser(properties: Record<string, unknown>): Promise<void> {
    if (isTestEnv) return;

    // Dev or staging with console-only analytics: no destination swap
    if (isDevEnv || (isStageEnv && !this.hasRemoteAmplitude)) {
      const propertiesWithAdminFlag = {
        ...properties,
        surface_admin: true,
      };
      this.setUser(propertiesWithAdminFlag);
      return;
    }

    // If already in internal user mode, just update user properties
    if (this.isInternalUserMode) {
      const propertiesWithAdminFlag = {
        ...properties,
        surface_admin: true,
      };
      this.setUser(propertiesWithAdminFlag);
      return;
    }

    // Import console destination class to check for existing instances
    const { ConsoleAnalyticsDestination } = await import(
      './destinations/console'
    );

    // Check if console destination already exists using instanceof
    const hasConsoleDestination = this.destinations.some(
      (dest) => dest instanceof ConsoleAnalyticsDestination
    );

    // Remove production destinations (Amplitude) before adding console
    // This prevents duplicate tracking from multiple destinations
    const { AmplitudeAnalyticsDestination } = await import(
      './destinations/amplitude'
    );

    this.destinations = this.destinations.filter(
      (dest) => !(dest instanceof AmplitudeAnalyticsDestination)
    );

    // Only add console destination if it doesn't already exist
    if (!hasConsoleDestination) {
      const consoleDestination = new ConsoleAnalyticsDestination();
      consoleDestination.initialize('hello, internal user!');
      this.destinations.push(consoleDestination);
    }

    // Mark as internal user mode to prevent re-processing
    this.isInternalUserMode = true;

    // Add surface_admin property to identify internal users
    const propertiesWithAdminFlag = {
      ...properties,
      surface_admin: true,
    };

    this.setUser(propertiesWithAdminFlag);
  }

  /**
   * Track an event across all initialized destinations
   *
   * Sends the event to all configured analytics destinations. If any destination fails,
   * the error is caught and ignored to maintain app stability.
   *
   * **Note:** Events are not tracked in test environments.
   *
   * @param eventName - The name of the event to track (must be a valid event from the event catalog)
   * @param params - Optional parameters to include with the event
   *
   * @example
   * ```typescript
   * // Track a simple event
   * Analytics.track('page_viewed');
   *
   * // Track an event with parameters
   * Analytics.track('button_clicked', {
   *   buttonName: 'submit',
   *   page: 'checkout',
   *   userId: '123'
   * });
   * ```
   */
  track: TrackFn = (eventName, params) => {
    if (isTestEnv) return;

    this.destinations.forEach((destination) => {
      try {
        destination.track(eventName, params);
      } catch {
        // Ignore destination errors to keep app stable
      }
    });
  };

  /**
   * Track a pageview across all initialized destinations
   *
   * Sends the pageview to all configured analytics destinations. If any destination fails,
   * the error is caught and ignored to maintain app stability.
   *
   * **Note:** Pageviews are not tracked in test environments.
   *
   * @param params - Pageview parameters including path and optional title
   * @param params.path - The URL path of the page
   * @param params.title - Optional page title
   *
   * @example
   * ```typescript
   * // Track a pageview with just the path
   * Analytics.pageview({ path: '/dashboard' });
   *
   * // Track a pageview with path and title
   * Analytics.pageview({
   *   path: '/dashboard',
   *   title: 'User Dashboard'
   * });
   * ```
   */
  pageview: PageViewFn = (params) => {
    if (isTestEnv) return;

    this.destinations.forEach((destination) => {
      try {
        destination.pageview(params);
      } catch {
        // Ignore destination errors to keep app stable
      }
    });
  };

  /**
   * Set user properties across all initialized destinations
   *
   * Updates user properties/attributes in all configured analytics destinations.
   * If any destination fails, the error is caught and ignored to maintain app stability.
   *
   * **Note:** User properties are not set in test environments.
   *
   * @param properties - Object containing user properties to set
   *
   * @example
   * ```typescript
   * // Set basic user properties
   * Analytics.setUser({
   *   userId: '123',
   *   email: 'user@example.com'
   * });
   *
   * // Set user properties with complex data
   * Analytics.setUser({
   *   userId: '123',
   *   plan: 'premium',
   *   signupDate: '2024-01-15',
   *   preferences: { theme: 'dark', notifications: true }
   * });
   * ```
   */
  setUser(properties: Record<string, unknown>): void {
    if (isTestEnv) return;

    this.destinations.forEach((destination) => {
      try {
        destination.setUser(properties);
      } catch {
        // Ignore destination errors to keep app stable
      }
    });
  }
}

// Create singleton instance
const analyticsService = new AnalyticsService();

/**
 * Singleton instance of the analytics service
 *
 * This is the main export for the analytics service. It provides a unified interface
 * for tracking events, pageviews, and user properties across multiple analytics platforms.
 *
 * **Key Features:**
 * - Environment-aware initialization
 * - Error isolation for app stability
 * - Type-safe event tracking
 * - Multiple destination support
 *
 * @see {@link AnalyticsService} - The underlying service class
 *
 * @example
 * ```typescript
 * import { Analytics } from '@/lib/analytics/analytics';
 *
 * // Initialize the service
 * Analytics.initialize(config);
 *
 * // Use throughout your app
 * Analytics.track('user_action', { action: 'login' });
 * Analytics.pageview({ path: '/home' });
 * Analytics.setUser({ userId: '123' });
 * ```
 */
export { analyticsService as Analytics };
